import IconClose from "@tvg/fuse-icons/lib/Close";
import { setSearchToSessionStorage } from "client/api/data-layer-service";
import { saveForGoogleAnalyticsToSessionStorage } from "client/api/google-analytics-service";
import {
  addRecentSearch,
  deleteRecentSearch,
} from "client/api/recent-searches-service";
import {
  ANALYTICS_RECENT_SEARCH,
  ANALYTICS_RECOMMENDED_SEARCH,
  ANALYTICS_SUGGESTED_SEARCH,
  DATA_LAYER,
} from "client/utils/constants";
import React, { Fragment, useCallback, useMemo } from "react";
import {
  RemoveTermButton,
  StyledBoldTerm,
  StyledProposal,
  TermLink,
} from "./searches.styles";

export interface ProposedSearchProps {
  term: string;
  isRecentSearch: boolean;
  searchedTerm?: string;
  isRecommendations: boolean;
  strategyId?: string;
  setAllowDisplayRecentSearches: (isAllow: boolean) => void;
}

const ProposedSearch = ({
  term,
  searchedTerm,
  isRecentSearch,
  isRecommendations,
  strategyId,
  setAllowDisplayRecentSearches,
}: ProposedSearchProps) => {
  const handleClick = useCallback(() => {
    if (isRecentSearch) {
      saveForGoogleAnalyticsToSessionStorage(ANALYTICS_RECENT_SEARCH);
      setSearchToSessionStorage(DATA_LAYER.RECENT_SEARCH);
      return;
    }

    if (isRecommendations) {
      addRecentSearch(term);
      saveForGoogleAnalyticsToSessionStorage(ANALYTICS_RECOMMENDED_SEARCH);
      setSearchToSessionStorage(DATA_LAYER.SUGGESTED_SEARCH);
      setAllowDisplayRecentSearches(false);
      return;
    }

    addRecentSearch(term);
    saveForGoogleAnalyticsToSessionStorage(ANALYTICS_SUGGESTED_SEARCH);
    setSearchToSessionStorage(DATA_LAYER.SUGGESTED_SEARCH);
    setAllowDisplayRecentSearches(true);
  }, [isRecentSearch, term, isRecommendations, setAllowDisplayRecentSearches]);

  const handleRemoveSearch = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      e.preventDefault();
      deleteRecentSearch(term);
    },
    [term],
  );

  const cnstrcTemr = useMemo(() => {
    return isRecommendations
      ? {
          ["data-cnstrc-item"]: "recommendation",
          ["data-cnstrc-item-id"]: term,
          ["data-cnstrc-strategy-id"]: strategyId,
        }
      : {
          ["data-cnstrc-item-section"]: "Search Suggestions",
        };
  }, [isRecommendations, term, strategyId]);

  if (!isRecentSearch) {
    return (
      <StyledProposal data-cnstrc-item-name={term} {...cnstrcTemr}>
        <TermLink
          href={`/search/${term}`}
          onClick={handleClick}
          data-testid={`suggested-search-term-${term}`}
        >
          {isRecommendations
            ? term
            : term
                .split(new RegExp(`(${searchedTerm})`))
                .map((subStr, idx) =>
                  idx % 2 === 0 ? (
                    <Fragment key={idx}>{subStr}</Fragment>
                  ) : (
                    <StyledBoldTerm key={idx}>{subStr}</StyledBoldTerm>
                  ),
                )}
        </TermLink>
      </StyledProposal>
    );
  }

  return (
    <StyledProposal>
      <TermLink
        href={`/search/${term}`}
        onClick={handleClick}
        data-testid={`recent-search-term-${term}`}
      >
        {term}
      </TermLink>
      <RemoveTermButton
        size="small"
        icon={<IconClose />}
        color="var(--color-greys-grey8)"
        srLabel={`Remove search term ${term}`}
        ariaLabel={`Remove search term ${term}`}
        dataTestId="suggested-searches-remove-term-button"
        callback={handleRemoveSearch}
      />
    </StyledProposal>
  );
};

export default React.memo(ProposedSearch);
